import {
  ANALOG_BANDWIDTH_OPTIONS,
  DEFAULT_ANALOG_MULTI_FREQUENCY_BANDWIDTH_MHZ,
  DEFAULT_DIGITAL_MULTI_FREQUENCY_BANDWIDTH_MHZ,
  DEFAULT_MANUAL_BANDWIDTH_MHZ,
  DEFAULT_MULTI_FREQUENCY_SIGNAL_TYPE,
  DIGITAL_BANDWIDTH_OPTIONS,
} from '../constants/suppressions.js';
import { SignalType } from '../types';
import { hzToMhz } from './frequency.js';

type BandwidthDeviceDefaults = {
  defaultAnalogSuppressionBandwidth?: number;
  defaultDigitalSuppressionBandwidth?: number;
  defaultAnalogMultiFrequencySuppressionBandwidth?: number;
  defaultDigitalMultiFrequencySuppressionBandwidth?: number;
  defaultMultiFrequencySuppressionBandwidth?: number;
};

type SignalTypeDeviceDefaults = {
  defaultMultiFrequencySuppressionSignalType?: SignalType;
};

/**
 * Snaps a bandwidth in MHz onto the nearest selectable option. An off-list value leaves the
 * controlled `<select>` with `selectedIndex === -1` — blank, while the form still validates and
 * submits the value the operator never saw.
 */
export function snapToBandwidthOption(bandwidthMhz: number, options: readonly number[]): number {
  if (options.includes(bandwidthMhz)) return bandwidthMhz;

  return options.reduce((nearest, option) =>
    Math.abs(option - bandwidthMhz) < Math.abs(nearest - bandwidthMhz) ? option : nearest,
  );
}

// Resolve the width selector's default for a signal type to a value guaranteed to
// be one of the selectable options: the device-configured default (Hz→MHz),
// snapped to the nearest option.
export function resolveDefaultBandwidthMhz(
  signalType: SignalType,
  deviceSettings?: BandwidthDeviceDefaults | null,
): number {
  const options =
    signalType === SignalType.DIGITAL ? DIGITAL_BANDWIDTH_OPTIONS : ANALOG_BANDWIDTH_OPTIONS;
  const deviceDefaultHz =
    signalType === SignalType.DIGITAL
      ? deviceSettings?.defaultDigitalSuppressionBandwidth
      : deviceSettings?.defaultAnalogSuppressionBandwidth;

  if (deviceDefaultHz == null) return DEFAULT_MANUAL_BANDWIDTH_MHZ;

  return snapToBandwidthOption(hzToMhz(deviceDefaultHz), options);
}

/** The signal type the multi-frequency form opens on, falling back when the server omits it. */
export function resolveDefaultMultiFrequencySignalType(
  deviceSettings?: SignalTypeDeviceDefaults | null,
): SignalType {
  return (
    deviceSettings?.defaultMultiFrequencySuppressionSignalType ??
    DEFAULT_MULTI_FREQUENCY_SIGNAL_TYPE
  );
}

/**
 * A sibling of `resolveDefaultBandwidthMhz` rather than a `mode` parameter on it, because the two
 * fall back differently: the manual widths share one constant across both signal types, while the
 * multi-frequency widths have one constant each. A single lookup keyed by mode and type would have
 * to encode that asymmetry in a table.
 */
export function resolveDefaultMultiFrequencyBandwidthMhz(
  signalType: SignalType,
  deviceSettings?: BandwidthDeviceDefaults | null,
): number {
  const isDigital = signalType === SignalType.DIGITAL;
  const options = isDigital ? DIGITAL_BANDWIDTH_OPTIONS : ANALOG_BANDWIDTH_OPTIONS;
  // A zahrada that predates the pair serves only the single width; it is the operator's configured
  // width for both types, so it outranks the constant.
  const deviceDefaultHz =
    (isDigital
      ? deviceSettings?.defaultDigitalMultiFrequencySuppressionBandwidth
      : deviceSettings?.defaultAnalogMultiFrequencySuppressionBandwidth) ??
    deviceSettings?.defaultMultiFrequencySuppressionBandwidth;

  // Not redundant with the snap below, which cannot represent "no value": `hzToMhz(undefined)` is
  // `Number(undefined) || 0` → 0, and `snapToBandwidthOption(0, …)` returns 2 — the narrowest
  // option — with nothing to distinguish it from a device that asked for 2 MHz. Every key is
  // genuinely absent on unresolved profiles.
  if (deviceDefaultHz == null) {
    return isDigital
      ? DEFAULT_DIGITAL_MULTI_FREQUENCY_BANDWIDTH_MHZ
      : DEFAULT_ANALOG_MULTI_FREQUENCY_BANDWIDTH_MHZ;
  }

  return snapToBandwidthOption(hzToMhz(deviceDefaultHz), options);
}
